import { cn } from "@/lib/utils";
import { 
  FileText, 
  Send, 
  CheckCircle, 
  Printer, 
  Package, 
  XCircle 
} from "lucide-react";
import type { CredentialRequestStatus } from "@/models/credentialRequest";
import { getStatusDisplayName } from "@/models/credentialRequest";

type BadgeVariant = "compact" | "default" | "large";

interface StatusBadgeProps {
  status: CredentialRequestStatus | string | null;
  variant?: BadgeVariant;
  showIcon?: boolean;
  className?: string;
}

// Status configuration with lucide icons and semantic colors
const statusConfig: Record<string, {
  icon: typeof FileText;
  colors: string;
}> = {
  DRAFT: {
    icon: FileText,
    colors: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700/50 dark:text-slate-300 dark:border-slate-600',
  },
  SUBMITTED: {
    icon: Send,
    colors: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/25 dark:text-blue-300 dark:border-blue-500/50',
  },
  APPROVED: {
    icon: CheckCircle,
    colors: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/25 dark:text-green-300 dark:border-green-500/50',
  },
  PRINTED: {
    icon: Printer,
    colors: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/25 dark:text-purple-300 dark:border-purple-500/50',
  },
  DELIVERED: {
    icon: Package,
    colors: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/25 dark:text-teal-300 dark:border-teal-500/50',
  },
  REJECTED: {
    icon: XCircle,
    colors: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/25 dark:text-red-300 dark:border-red-500/50',
  },
};

// Variant styles
const variantStyles: Record<BadgeVariant, {
  container: string;
  text: string;
  icon: string;
  shape: string;
}> = {
  compact: {
    container: 'px-2 py-0.5',
    text: 'text-xs',
    icon: 'size-3',
    shape: 'rounded-full',
  },
  default: {
    container: 'px-2.5 py-1',
    text: 'text-xs',
    icon: 'size-3.5',
    shape: 'rounded-md',
  },
  large: {
    container: 'px-3 py-1.5',
    text: 'text-sm font-semibold',
    icon: 'size-4',
    shape: 'rounded-md',
  },
};

/**
 * Atom: Unified status badge component for credential requests
 * Supports multiple variants with icons
 * - compact: Always shows icon (for table rows)
 * - default: Optional icon (showIcon prop)
 * - large: Always shows icon (for headers)
 */
export function StatusBadge({ 
  status, 
  variant = "default", 
  showIcon = false,
  className 
}: StatusBadgeProps) {
  const normalizedStatus = status?.toUpperCase() || 'DRAFT';
  const config = statusConfig[normalizedStatus] || statusConfig.DRAFT;
  const styles = variantStyles[variant];
  const displayName = getStatusDisplayName(status);
  
  const Icon = config.icon;
  
  // Show icon for compact and large variants, or when explicitly requested
  const shouldShowIcon = variant === 'compact' || variant === 'large' || showIcon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 border font-medium transition-colors',
        styles.container,
        styles.text,
        styles.shape,
        config.colors,
        variant === 'large' && 'shadow-sm',
        className
      )}
      title={`Estado: ${displayName}`}
    >
      {shouldShowIcon && (
        <Icon className={cn(styles.icon, 'flex-shrink-0')} aria-hidden="true" />
      )}
      <span>{displayName}</span>
    </span>
  );
}
